import styles from "./CarouselEditor.module.scss";
import { useCloseDeletedSelection } from "../../../components/common/useCloseDeletedSelection";
import CollectionItemActions from "./CollectionItemActions";
import { useCollectionItems } from "./collectionItems";
import { Form, Button, Card, CardBody, CardFooter, Col, FormLabel, Row, FormGroup, FormControl } from "react-bootstrap";
import { ICarouselItem, IFieldEditor } from "../../../../models/Interfaces";
import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faPlus, faSave, faTimes } from "@fortawesome/free-solid-svg-icons";
import SiteMediaPicker from "../../../components/common/SiteMediaPicker";
import { v4 as uuidv4 } from "uuid";
import HTMLEditor from "../../../components/common/HTMLEditor";
import { carouselCaptionFrame } from "../../../components/common/editorSiteFrame";
import PageSelector from "../../../components/common/PageSelector";
import ColourPicker from "../../../components/common/ColourPicker";

const CarouselEditor = (props: IFieldEditor) => {

  // Explicit typing of selectedItem
  const [items, setItems] = useCollectionItems<ICarouselItem>(props);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorSession, setEditorSession] = useState(0);
  const [selectedItem, setSelectedItem] = useState<ICarouselItem | null>(null);

  const addItem = () => {
    const newitem: ICarouselItem = {
      Id: uuidv4(),
      AltText: "",
      BackgroundColour: "",
      ImageUrl: "",
      Link: "",
      Text: "",
      TextColour: "",
      Title: ""
    };
    setSelectedItem(newitem);
    setEditorSession(session => session + 1);
    setEditorOpen(true);
  };

  const onEditItem = (item: ICarouselItem) : void => {
    setSelectedItem(item);
    setEditorSession(session => session + 1);
    setEditorOpen(true);
  };

  const onCloseEdit = () : void => {
    setEditorOpen(false);
  };

  // Handling image selection
  const onImageSelect = (selectedurl: string) : void => {
    if (selectedItem) {
      setSelectedItem(prev => ({
          ...prev as ICarouselItem,
          ImageUrl: selectedurl
      }));
    }
  };

  // Handling image clearing
  const onImageClear = () : void => {
    if (selectedItem) {
      setSelectedItem(prev => ({
          ...prev as ICarouselItem,
          ImageUrl: ""
      }));
    }
  };

  const onPageSelect = (url: string) : void => {
    setSelectedItem(prev => ({
      ...prev as ICarouselItem,
      Link: url
    }));
  };

  const setItemPropertyValue = (property: string, value: string) : void => {
    setSelectedItem(prev => ({
      ...prev as ICarouselItem,
      [property]: value
    }));
  };

  const onItemSave = () : void => {
    const item = selectedItem as ICarouselItem;
    setItems(previous => previous.some(value => value.Id === item.Id)
      ? previous.map(value => value.Id === item.Id ? item : value) : [...previous, item]);

    setEditorOpen(false);
  };

  const closeDeletedSelection = useCloseDeletedSelection(selectedItem, (item) => item.Id, () => setEditorOpen(false));

  const onItemDelete = (itemid: string) : void => {
    const filtered = items.filter(itm => itm.Id !== itemid);

    if (filtered) {
      setItems(filtered);
      closeDeletedSelection(itemid);

    }
  };


  useEffect(() => {
    props.onEditorOpen?.(editorOpen);
  }, [props.onEditorOpen, editorOpen]);

  return (
    <div data-nested-editor-open={editorOpen ? "true" : "false"}>
      {!editorOpen && (
        <>
          <Row className="mt-3">
            <Col xs={12}>
              <Button
                type="button"
                title="Add carousel item"
                onClick={addItem}
              >
                <FontAwesomeIcon icon={faPlus} />&nbsp;Add item
              </Button>
            </Col>
          </Row>

          {items.length > 0 && (
            <div className={`mt-3 ${styles.itemsGrid}`}>
              {items.map((item, index) => (
                <div key={item.Id}>
                  <Card className="h-100">
                    <div className={`card-img-top ${styles.thumbnail}`}>
                      <img className="img-fluid" src={item.ImageUrl ?? ""} alt={item.AltText || `Slide ${index + 1}`} />
                    </div>
                    <CardBody className="py-2">
                      <div className="small">Slide {index + 1}</div>
                      {(item.Title || item.Text) && <>
                      <Row>
                        <Col xs={12}>
                          <FormLabel dangerouslySetInnerHTML={{ __html: item.Title ?? "" }} />
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={12}>
                          <FormLabel dangerouslySetInnerHTML={{ __html: item.Text ?? "" }} />
                        </Col>
                      </Row>
                      </>}
                    </CardBody>
                    <CardFooter className="d-flex align-items-center justify-content-between gap-2">
                      <div className="d-flex gap-1">
                      <Button
                        type="button"
                        title="Edit carousel item"
                        aria-label={`Edit slide ${index + 1}`}
                        size="sm"
                        variant="outline-secondary"
                        className="btn-nomin"
                        onClick={() => onEditItem(item) }
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Button>
                      <Button
                        type="button"
                        title="Delete carousel item"
                        aria-label={`Delete slide ${index + 1}`}
                        size="sm"
                        variant="outline-secondary"
                        className="btn-nomin"
                        onClick={() => onItemDelete(item.Id)}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </Button>
                      </div>
                      <CollectionItemActions compact items={items} index={index} onChange={setItems} />
                    </CardFooter>
                  </Card>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {selectedItem !== null && (
        <Form id="AddEditCarouselItemForm" hidden={!editorOpen}>
          <FormGroup>
            <Row>
              <Col xs={12} className="mt-3">
                <SiteMediaPicker
                  existingUrl={selectedItem.ImageUrl ?? undefined}
                  onMediaClear={onImageClear}
                  onMediaSelect={onImageSelect}
                />
              </Col>
            </Row>
            <Row>
              <Col md={12} xl={6} className="mt-3">
                <FormLabel>Alt Text (in case image can't be displayed)</FormLabel>
                <FormControl
                  type="text"
                  placeholder="image description..."
                  value={selectedItem.AltText ?? ""} 
                  onChange={(e) => setItemPropertyValue("AltText", e.target.value)}
                />
              </Col>
              <Col md={12} xl={6} className="mt-3">
                <FormLabel>Site page or link (when image clicked)</FormLabel>
                <PageSelector existingUrl={selectedItem.Link} onPageClear={() => onPageSelect("")} onPageSelect={onPageSelect} />
              </Col>
            </Row>
            <Row>
              <Col md={12} className="mt-3">
                <FormLabel>Caption Title</FormLabel>
                <HTMLEditor height={props.editorHeight} documentKey={editorSession}
                  name="Title"
                  value={selectedItem.Title ?? ""}
                  siteFrame={carouselCaptionFrame(props.siteFrame, "title")}
                  onChange={(content: string) => setItemPropertyValue("Title", content)}
                />
              </Col>
            </Row>
            <Row>
              <Col md={12} className="mt-3">
                <FormLabel>Caption Text</FormLabel>
                <HTMLEditor height={props.editorHeight} documentKey={editorSession}
                  name="Text"
                  value={selectedItem.Text ?? ""}
                  siteFrame={carouselCaptionFrame(props.siteFrame, "text")}
                  onChange={(content: string) => setItemPropertyValue("Text", content)}
                />
              </Col>
            </Row>
            <Row>
              <Col md={12} xl={6} className="mt-3">
                <FormLabel>Set background colour (for any caption details)</FormLabel>
                <ColourPicker selectedColour={selectedItem.BackgroundColour} onColourChange={(colour) => setItemPropertyValue("BackgroundColour", colour ?? "") } />
              </Col>
              <Col md={12} xl={6} className="mt-3">
                <FormLabel>Set text colour (for any caption details)</FormLabel>
                <ColourPicker selectedColour={selectedItem.TextColour} onColourChange={(colour) => setItemPropertyValue("TextColour", colour ?? "") } />
              </Col>
            </Row>
          </FormGroup>
          <Row>
            <Col xs={12}>
              <hr />
            </Col>
          </Row>
          <Row>
            <Col xs={12} className="small text-muted mb-2">
              Save or cancel this gallery item before returning to the main component editor.
            </Col>
          </Row>
          <Row>
            <Col xs={12} className="text-end">
              <Button variant="outline-success" className="me-1" title="save item" onClick={onItemSave}>
                <FontAwesomeIcon icon={faSave} />&nbsp;Save item
              </Button>
              <Button variant="outline-primary" title="cancel item editing" onClick={onCloseEdit}>Cancel item</Button>
            </Col>
          </Row>
        </Form>
      )}
    </div>
  );
};

export default CarouselEditor;
